import { useState } from 'react';
import { Gem, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { eventsApi } from '@/api/events.api';
import type { CreateEventBody } from '@/api/events.api';

/**
 * Form de configuración del minijuego **Cofre del Tesoro** (familia social).
 * Clona la pantalla "Crear Cofre del Tesoro" de fedesuete:
 *  - 3 opciones fijas (Diamantes / Monedas / Joyas).
 *  - Premio por ganador (la minoría) — típicamente Bono 200%.
 *  - Depósito mínimo + duración.
 *
 * Guarda vía eventsApi.create con type='cofre' y arma el `config` JSONB que
 * consume `CofreResolver` (options / prizeKind / amount / depositMin).
 */

interface CofreOption {
  emoji: string;
  label: string;
}

// Opciones del screenshot (no editables — clon 1:1 de fedesuete).
const COFRE_OPTIONS: CofreOption[] = [
  { emoji: '💎', label: 'Diamantes' },
  { emoji: '💰', label: 'Monedas' },
  { emoji: '💍', label: 'Joyas' },
];

const OPTION_ACCENTS = ['text-cyan-300', 'text-amber-300', 'text-fuchsia-300'];

type PrizeKind = 'bono200' | 'bono100' | 'fichas';

const PRIZE_LABELS: Record<PrizeKind, string> = {
  bono200: 'Bono 200% (recomendado)',
  bono100: 'Bono 100%',
  fichas: 'Fichas',
};

interface CofreFormProps {
  /** Callback tras crear/guardar (refrescar lista, cerrar tab, etc.). */
  onSaved?: () => void;
  /** Callback de error opcional. */
  onError?: (message: string) => void;
}

export function CofreForm({ onSaved, onError }: CofreFormProps) {
  const [name, setName] = useState('');
  const [depositMin, setDepositMin] = useState('2000');
  const [durationMin, setDurationMin] = useState('60');
  const [amount, setAmount] = useState('4500');
  const [prizeKind, setPrizeKind] = useState<PrizeKind>('bono200');
  const [submitting, setSubmitting] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  async function submit(activate: boolean) {
    if (!name.trim()) {
      setLocalError('El nombre es requerido');
      return;
    }
    setLocalError(null);
    setSubmitting(true);

    const config = {
      options: COFRE_OPTIONS,
      prizeKind,
      amount: amount ? Number(amount) : 0,
      depositMin: depositMin ? Number(depositMin) : 0,
    };

    // `type: 'cofre'` aún no está en el union de EventType del api compartido
    // (lo extiende el orquestador). Cast acotado para no tocar events.api.ts.
    const body = {
      type: 'cofre',
      name: name.trim(),
      description:
        'Cofre del Tesoro — gana la opción menos elegida. Premio: ' +
        PRIZE_LABELS[prizeKind] +
        '.',
      depositMin: depositMin || undefined,
      prizeKind,
      prizeAmount: amount || undefined,
      durationMin: durationMin ? Number(durationMin) : undefined,
      status: activate ? 'active' : 'draft',
      config,
    } as unknown as CreateEventBody;

    try {
      await eventsApi.create(body);
      onSaved?.();
      // Reset suave de los campos editables.
      setName('');
    } catch (e) {
      const msg = (e as Error).message || 'No se pudo guardar el cofre';
      setLocalError(msg);
      onError?.(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-5">
      {/* Encabezado clon del screenshot */}
      <div>
        <h3 className="flex items-center gap-2 text-lg font-bold text-amber-400">
          Crear Cofre del Tesoro
          <Gem className="h-4 w-4 text-cyan-300" />
        </h3>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          Cuando un jugador deposita ≥ ${Number(depositMin || 0).toLocaleString('es-AR')},
          le aparecen 3 opciones para elegir: 💎 Diamantes, 💰 Monedas o 💍 Joyas.
          Al terminar, la opción con <strong>MENOS votos gana</strong>. Si hay
          empate, se elige al azar.
        </p>
      </div>

      {/* Nombre */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">
          Nombre <span className="text-red-500">*</span>
        </label>
        <Input
          value={name}
          onChange={(e) => setName((e.target as HTMLInputElement).value)}
          placeholder="ej: Cofre Nocturno"
        />
      </div>

      {/* Depósito mínimo + Duración */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Depósito mínimo ($)</label>
          <Input
            type="number"
            min="0"
            value={depositMin}
            onChange={(e) => setDepositMin((e.target as HTMLInputElement).value)}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Duración (minutos)</label>
          <Input
            type="number"
            min="0"
            value={durationMin}
            onChange={(e) => setDurationMin((e.target as HTMLInputElement).value)}
          />
        </div>
      </div>

      {/* Premio por ganador + Tipo de premio */}
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Premio por ganador ($)</label>
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={(e) => setAmount((e.target as HTMLInputElement).value)}
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Tipo de premio</label>
          <Select
            value={prizeKind}
            onValueChange={(v) => setPrizeKind(v as PrizeKind)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PRIZE_LABELS) as PrizeKind[]).map((k) => (
                <SelectItem key={k} value={k}>
                  {PRIZE_LABELS[k]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      {prizeKind === 'bono200' && (
        <p className="-mt-3 text-xs text-muted-foreground">
          Solo Bono 200% en próxima carga (sin fichas)
        </p>
      )}

      {/* Las 3 opciones (visual, no editables — clon del screenshot) */}
      <div className="grid grid-cols-3 gap-3">
        {COFRE_OPTIONS.map((opt, i) => (
          <div
            key={opt.label}
            className="flex flex-col items-center gap-2 rounded-lg border border-border bg-card py-6"
          >
            <span className="text-3xl">{opt.emoji}</span>
            <span className={`text-sm font-bold ${OPTION_ACCENTS[i]}`}>
              {opt.label}
            </span>
          </div>
        ))}
      </div>

      {localError && <p className="text-sm text-red-500">{localError}</p>}

      {/* Acciones */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          disabled={submitting}
          onClick={() => submit(false)}
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Guardar Borrador
        </Button>
        <Button
          className="bg-amber-500 text-black hover:bg-amber-400"
          disabled={submitting}
          onClick={() => submit(true)}
        >
          {submitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Crear y Activar
        </Button>
      </div>
    </div>
  );
}

export default CofreForm;
